import {
  normalizeMachineSearchInput,
  normalizeMachineTypeInput,
  validateMachineAttributes,
  type AddMachineAttachmentInput,
  type CreateMachineInput,
  type MachineAttachmentRecord,
  type MachineCustomerOption,
  type MachineDetailRecord,
  type MachineTaxScheme,
  type MachineListRecord,
  type MachinePage,
  type MachineSearchInput,
  type MachinesGateway,
  type MachineTypeAdminFieldRecord,
  type MachineTypeAdminRecord,
  type MachineTypeRecord,
  type SaveMachineTypeInput,
  type UpdateMachineInput,
} from '@forq/machines-data';
import { demoWarengruppeName } from '@forq/masters-data';

function clone<T>(value: T): T { return structuredClone(value); }
function clean(value: string | undefined): string { return value?.trim() ?? ''; }
function prefixFor(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}

function field(
  tenantId: string,
  typeCode: string,
  key: string,
  label: string,
  type: MachineTypeAdminFieldRecord['type'],
  unit: string,
  required: boolean,
  options: readonly string[],
  sortOrder: number,
  group = '',
): MachineTypeAdminFieldRecord {
  return {
    id: `${tenantId}-type-${typeCode}-field-${key}`,
    key,
    label,
    type,
    unit,
    required,
    options,
    sortOrder,
    active: true,
    group,
  };
}

function seedTypes(tenantId: string): MachineTypeAdminRecord[] {
  const type = (
    code: string,
    name: string,
    description: string,
    fields: readonly MachineTypeAdminFieldRecord[],
  ): MachineTypeAdminRecord => ({
    id: `${tenantId}-type-${code}`,
    code,
    name,
    description,
    active: true,
    machineCount: 0,
    fields,
  });
  return [
    type('forklift', 'Gabelstapler', 'Frontstapler mit typischen Hub- und Antriebsdaten', [
      field(tenantId, 'forklift', 'bauart', 'Bauart', 'select', '', false, ['Frontstapler', 'Containerstapler', 'Schwerlaststapler', 'Seitenstapler', 'Vierwegestapler', 'Geländestapler', 'Sonstige'], 5, 'Gerätedaten'),
      field(tenantId, 'forklift', 'zustand', 'Zustand', 'select', '', false, ['neu', 'gebraucht', 'generalüberholt', 'defekt'], 8, 'Gerätedaten'),
      field(tenantId, 'forklift', 'capacity', 'Tragfähigkeit', 'number', 'kg', true, [], 10, 'Technische Daten'),
      field(tenantId, 'forklift', 'lastschwerpunkt', 'Lastschwerpunkt', 'number', 'mm', false, [], 15, 'Technische Daten'),
      field(tenantId, 'forklift', 'drive', 'Antrieb', 'select', '', true, ['Elektro', 'Diesel', 'Treibgas', 'Benzin', 'Hybrid', 'Lithium-Ionen'], 40, 'Technische Daten'),
      field(tenantId, 'forklift', 'eigengewicht', 'Eigengewicht', 'number', 'kg', false, [], 44, 'Technische Daten'),
      field(tenantId, 'forklift', 'laenge', 'Länge', 'number', 'mm', false, [], 45, 'Technische Daten'),
      field(tenantId, 'forklift', 'breite', 'Breite', 'number', 'mm', false, [], 46, 'Technische Daten'),
      field(tenantId, 'forklift', 'fahrgestellnummer', 'Fahrgestellnummer', 'text', '', false, [], 48, 'Technische Daten'),
      field(tenantId, 'forklift', 'gewaehrleistung', 'Gewährleistung', 'boolean', '', false, [], 49, 'Technische Daten'),
      field(tenantId, 'forklift', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 50, 'Technische Daten'),
      field(tenantId, 'forklift', 'verfuegbarAb', 'Verfügbar ab', 'date', '', false, [], 58, 'Technische Daten'),
      field(tenantId, 'forklift', 'batteryVoltage', 'Batteriespannung', 'number', 'V', false, [], 60, 'Technische Daten'),
      field(tenantId, 'forklift', 'liftHeight', 'Hubhöhe', 'number', 'mm', false, [], 20, 'Hubgerüst'),
      field(tenantId, 'forklift', 'bauhoehe', 'Bauhöhe (eingefahren)', 'number', 'mm', false, [], 25, 'Hubgerüst'),
      field(tenantId, 'forklift', 'freihub', 'Freihub', 'number', 'mm', false, [], 27, 'Hubgerüst'),
      field(tenantId, 'forklift', 'mastType', 'Mastart', 'select', '', false, ['Standard', 'Duplex', 'Triplex', 'Quattro', 'Teleskop', 'Sonstige'], 30, 'Hubgerüst'),
      field(tenantId, 'forklift', 'gabellaenge', 'Gabellänge', 'number', 'mm', false, [], 32, 'Hubgerüst'),
      field(tenantId, 'forklift', 'gabeltraeger', 'Gabelträger', 'text', '', false, [], 34, 'Hubgerüst'),
      field(tenantId, 'forklift', 'gabeln', 'Gabeln', 'text', '', false, [], 36, 'Hubgerüst'),
      field(tenantId, 'forklift', 'reifenVorne', 'Bereifung vorne', 'select', '', false, ['Luftreifen', 'Superelastik', 'Vollgummi', 'Polyurethan', 'Bandagen', 'Schaumgefüllt'], 70, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'reifenHinten', 'Bereifung hinten', 'select', '', false, ['Luftreifen', 'Superelastik', 'Vollgummi', 'Polyurethan', 'Bandagen', 'Schaumgefüllt'], 71, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'reifengroesseVorne', 'Reifengröße vorne', 'text', '', false, [], 72, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'reifengroesseHinten', 'Reifengröße hinten', 'text', '', false, [], 73, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'anbaugeraete', 'Anbaugeräte', 'multiselect', '', false, ['Seitenschieber', 'Zinkenverstellgerät', 'Gabelverlängerung', 'Klammergabel', 'Doppelpalettengabel', 'Drehgerät', 'Schubgabel', 'Lastschutzgitter', 'Schüttgutschaufel', 'Papierrollenklammer', 'Ballenklammer', 'Tragedorn'], 74, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'weitereAnbaugeraete', 'Weitere Anbaugeräte', 'text', '', false, [], 75, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'sonderausstattung', 'Sonderausstattung', 'multiselect', '', false, ['Vollkabine', 'Halbkabine', 'Kabinendach', 'Frontscheibe', 'Heizung', 'Klimaanlage', 'Scheibenwischer', 'Arbeitsscheinwerfer vorn', 'Arbeitsscheinwerfer hinten', 'Rundumleuchte', 'LED-Beleuchtung', 'Rückfahrkamera', 'Rückfahrwarner', 'Sitzheizung', 'Komfortsitz', '3. Ventil', '4. Ventil', 'Waage'], 80, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'weitereAusstattung', 'Weitere Ausstattung', 'text', '', false, [], 81, 'Bereifung & Ausstattung'),
      field(tenantId, 'forklift', 'spezialanfertigung', 'Spezialanfertigung', 'multiselect', '', false, ['Kühlhaustauglich', 'Explosionsgeschützt (EX)', 'Edelstahl', 'Verzinkt', 'Doppelstock', 'Zwillingsbereifung', 'Sonderbau'], 90, 'Bereifung & Ausstattung'),
    ]),
    type('reach-truck', 'Schubmaststapler', 'Schmalgangfähige Lagertechnik', [
      field(tenantId, 'reach-truck', 'capacity', 'Tragfähigkeit', 'number', 'kg', true, [], 10),
      field(tenantId, 'reach-truck', 'liftHeight', 'Hubhöhe', 'number', 'mm', true, [], 20),
      field(tenantId, 'reach-truck', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 30),
      field(tenantId, 'reach-truck', 'camera', 'Gabelkamera', 'boolean', '', false, [], 40),
    ]),
    type('pallet-truck', 'Niederhubwagen', 'Hand- und Elektrohubwagen', [
      field(tenantId, 'pallet-truck', 'capacity', 'Tragfähigkeit', 'number', 'kg', true, [], 10),
      field(tenantId, 'pallet-truck', 'forkLength', 'Gabellänge', 'number', 'mm', false, [], 20),
      field(tenantId, 'pallet-truck', 'drive', 'Antrieb', 'select', '', true, ['Hand', 'Elektro'], 30),
      field(tenantId, 'pallet-truck', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 40),
    ]),
    type('stacker', 'Hochhubwagen', 'Deichsel- und Mitgängerstapler', [
      field(tenantId, 'stacker', 'capacity', 'Tragfähigkeit', 'number', 'kg', true, [], 10),
      field(tenantId, 'stacker', 'liftHeight', 'Hubhöhe', 'number', 'mm', true, [], 20),
      field(tenantId, 'stacker', 'mastType', 'Mastart', 'select', '', false, ['Standard', 'Duplex', 'Triplex'], 30),
      field(tenantId, 'stacker', 'overallHeight', 'Bauhöhe eingefahren', 'number', 'mm', false, [], 40),
      field(tenantId, 'stacker', 'drive', 'Antrieb', 'select', '', true, ['Elektro', 'Hand'], 50),
      field(tenantId, 'stacker', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 60),
      field(tenantId, 'stacker', 'batteryVoltage', 'Batteriespannung', 'number', 'V', false, [], 70),
    ]),
    type('order-picker', 'Kommissionierer', 'Horizontal- und Vertikalkommissionierer', [
      field(tenantId, 'order-picker', 'capacity', 'Tragfähigkeit', 'number', 'kg', true, [], 10),
      field(tenantId, 'order-picker', 'liftHeight', 'Hubhöhe', 'number', 'mm', false, [], 20),
      field(tenantId, 'order-picker', 'platformHeight', 'Plattformhöhe', 'number', 'mm', false, [], 30),
      field(tenantId, 'order-picker', 'forkLength', 'Gabellänge', 'number', 'mm', false, [], 40),
      field(tenantId, 'order-picker', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 50),
      field(tenantId, 'order-picker', 'batteryVoltage', 'Batteriespannung', 'number', 'V', false, [], 60),
    ]),
    type('sweeper', 'Kehrmaschine', 'Aufsitz- und handgeführte Kehrmaschinen', [
      field(tenantId, 'sweeper', 'operatorType', 'Bauart', 'select', '', true, ['Handgeführt', 'Aufsitz'], 40, 'Gerätedaten'),
      field(tenantId, 'sweeper', 'zustand', 'Zustand', 'select', '', false, ['neu', 'gebraucht', 'generalüberholt', 'defekt'], 8, 'Gerätedaten'),
      field(tenantId, 'sweeper', 'drive', 'Antrieb', 'select', '', true, ['Batterie', 'Benzin', 'Diesel'], 50, 'Technische Daten'),
      field(tenantId, 'sweeper', 'sweepWidth', 'Kehrbreite', 'number', 'mm', false, [], 25, 'Technische Daten'),
      field(tenantId, 'sweeper', 'workingWidth', 'Arbeitsbreite', 'number', 'mm', true, [], 10, 'Technische Daten'),
      field(tenantId, 'sweeper', 'brushPressure', 'Bürstendruck', 'number', 'kg', false, [], 35, 'Technische Daten'),
      field(tenantId, 'sweeper', 'areaPerformance', 'Flächenleistung', 'number', 'm²/h', false, [], 20, 'Technische Daten'),
      field(tenantId, 'sweeper', 'hopperVolume', 'Behältervolumen', 'number', 'l', false, [], 30, 'Technische Daten'),
      field(tenantId, 'sweeper', 'deadWeight', 'Eigengewicht', 'number', 'kg', false, [], 52, 'Technische Daten'),
      field(tenantId, 'sweeper', 'batteryVoltage', 'Batteriespannung', 'number', 'V', false, [], 55, 'Technische Daten'),
      field(tenantId, 'sweeper', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 60, 'Technische Daten'),
      field(tenantId, 'sweeper', 'fahrgestellnummer', 'Fahrgestellnummer', 'text', '', false, [], 58, 'Technische Daten'),
      field(tenantId, 'sweeper', 'verfuegbarAb', 'Verfügbar ab', 'date', '', false, [], 62, 'Technische Daten'),
    ]),
    type('scrubber', 'Scheuersaugmaschine', 'Aufsitz- und handgeführte Scheuersaugmaschinen', [
      field(tenantId, 'scrubber', 'operatorType', 'Bauart', 'select', '', true, ['Handgeführt', 'Aufsitz'], 60, 'Gerätedaten'),
      field(tenantId, 'scrubber', 'zustand', 'Zustand', 'select', '', false, ['neu', 'gebraucht', 'generalüberholt', 'defekt'], 8, 'Gerätedaten'),
      field(tenantId, 'scrubber', 'drive', 'Antrieb', 'select', '', false, ['Batterie', 'Netz', 'Benzin', 'Diesel'], 15, 'Technische Daten'),
      field(tenantId, 'scrubber', 'workingWidth', 'Arbeitsbreite', 'number', 'mm', true, [], 10, 'Technische Daten'),
      field(tenantId, 'scrubber', 'suctionWidth', 'Saugbreite', 'number', 'mm', false, [], 20, 'Technische Daten'),
      field(tenantId, 'scrubber', 'brushPressure', 'Bürstendruck', 'number', 'kg', false, [], 35, 'Technische Daten'),
      field(tenantId, 'scrubber', 'areaPerformance', 'Flächenleistung', 'number', 'm²/h', false, [], 30, 'Technische Daten'),
      field(tenantId, 'scrubber', 'freshWaterTank', 'Frischwassertank', 'number', 'l', false, [], 40, 'Technische Daten'),
      field(tenantId, 'scrubber', 'dirtyWaterTank', 'Schmutzwassertank', 'number', 'l', false, [], 50, 'Technische Daten'),
      field(tenantId, 'scrubber', 'deadWeight', 'Eigengewicht', 'number', 'kg', false, [], 52, 'Technische Daten'),
      field(tenantId, 'scrubber', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 70, 'Technische Daten'),
      field(tenantId, 'scrubber', 'batteryVoltage', 'Batteriespannung', 'number', 'V', false, [], 80, 'Technische Daten'),
      field(tenantId, 'scrubber', 'fahrgestellnummer', 'Fahrgestellnummer', 'text', '', false, [], 58, 'Technische Daten'),
      field(tenantId, 'scrubber', 'verfuegbarAb', 'Verfügbar ab', 'date', '', false, [], 62, 'Technische Daten'),
    ]),
    // Schere und Teleskop sind EIN Typ mit einer Bauart, nicht zwei Typen: die technischen Daten
    // sind dieselben, und getrennte Typen würden bei jeder Auswertung wieder zusammengerechnet.
    type('aerial-platform', 'Arbeitsbühne', 'Scheren- und Teleskopbühnen', [
      field(tenantId, 'aerial-platform', 'platformType', 'Bauart', 'select', '', true, ['Scherenbühne', 'Teleskopbühne', 'Gelenkteleskopbühne'], 10, 'Gerätedaten'),
      field(tenantId, 'aerial-platform', 'zustand', 'Zustand', 'select', '', false, ['neu', 'gebraucht', 'generalüberholt', 'defekt'], 8, 'Gerätedaten'),
      field(tenantId, 'aerial-platform', 'workingHeight', 'Arbeitshöhe', 'number', 'm', true, [], 20, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'platformHeight', 'Plattformhöhe', 'number', 'mm', false, [], 22, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'platformExtension', 'Plattformvorschub', 'number', 'mm', false, [], 24, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'capacity', 'Tragkraft', 'number', 'kg', false, [], 28, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'basketLoad', 'Korblast', 'number', 'kg', true, [], 30, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'lateralReach', 'Seitliche Reichweite', 'number', 'm', false, [], 40, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'overallHeight', 'Transporthöhe', 'number', 'm', false, [], 50, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'deadWeight', 'Eigengewicht', 'number', 'kg', false, [], 52, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'carrierVehicle', 'Trägerfahrzeug', 'text', '', false, [], 54, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'specialDrive', 'Sonderantrieb', 'boolean', '', false, [], 56, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'drive', 'Antrieb', 'select', '', true, ['Elektro', 'Diesel', 'Hybrid'], 60, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'terrain', 'Einsatz', 'select', '', false, ['Innen', 'Außen', 'Innen und Außen'], 70, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'hours', 'Betriebsstunden', 'number', 'h', false, [], 80, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'fahrgestellnummer', 'Fahrgestellnummer', 'text', '', false, [], 58, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'verfuegbarAb', 'Verfügbar ab', 'date', '', false, [], 62, 'Technische Daten'),
      field(tenantId, 'aerial-platform', 'sonderausstattung', 'Sonderausstattung', 'multiselect', '', false, ['Lastmesssystem', 'Schrägfahreinrichtung', 'Generator (230V)', 'Druckluft an Bord', 'Schweißanschluss', 'Rundumleuchte', 'Arbeitsscheinwerfer', 'Nichtmarkierende Reifen', 'Allradantrieb', 'Allradlenkung'], 80, 'Ausstattung'),
    ]),
    type('other', 'Sonstige Maschine', 'Frei nutzbarer Maschinentyp', [
      field(tenantId, 'other', 'description', 'Technische Beschreibung', 'text', '', false, [], 10),
    ]),
  ];
}

function runtimeType(machineType: MachineTypeAdminRecord): MachineTypeRecord {
  return {
    id: machineType.id,
    code: machineType.code,
    name: machineType.name,
    description: machineType.description,
    fields: machineType.fields
      .filter((item) => item.active)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map(({ id: _id, active: _active, ...item }) => item),
  };
}

function customersFor(tenantId: string): readonly MachineCustomerOption[] {
  const prefix = prefixFor(tenantId);
  const names = prefix === '2'
    ? ['Westfalen Schwerlast GmbH', 'Meyer Maschinenlogistik KG']
    : prefix === '3'
      ? ['Sauerland Holztechnik GmbH', 'Metallbau Arnsberg KG']
      : ['Werl Industriebedarf GmbH', 'Bäckerei Haverkamp GmbH'];
  return names.map((displayName, index) => ({
    id: `${tenantId}-customer-${index + 1}`,
    number: `${prefix}${String(index + 1).padStart(4, '0')}`,
    displayName,
    city: index === 0 ? 'Werl' : 'Soest',
  }));
}

function listRecord(machine: MachineDetailRecord): MachineListRecord {
  // attributes bleiben jetzt in der Liste (für Kennzahlen in Tabelle/Karten).
  const { tenantId: _tenantId, notes: _notes, createdAt: _createdAt, ...list } = machine;
  return list;
}

function createSeed(
  tenantId: string,
  types: readonly MachineTypeAdminRecord[],
  customers: readonly MachineCustomerOption[],
): MachineDetailRecord[] {
  const prefix = prefixFor(tenantId);
  const now = new Date().toISOString();
  const dayMs = 86_400_000;
  // Demo-Prüftermine relativ zu heute, damit die Fälligkeiten-Liste immer etwas zeigt.
  const isoDate = (offsetDays: number) => new Date(Date.now() + offsetDays * dayMs).toISOString().slice(0, 10);
  return [
    {
      id: `${tenantId}-machine-1`, tenantId, number: `${prefix}0001`, typeId: types[0].id, typeName: types[0].name,
      manufacturer: 'Toyota', model: '8FBE16T', serialNumber: `TOY-${prefix}001`, constructionYear: 2021,
      status: 'customer', customerId: customers[0].id, customerNumber: customers[0].number,
      customerName: customers[0].displayName, location: 'Werl', notes: 'Demomaschine für Serviceprozesse.',
      attributes: { capacity: 1600, liftHeight: 4700, mastType: 'Triplex', drive: 'Elektro', hours: 2840, batteryVoltage: 48 },
      imageUrl: '', purchasePrice: 9500, salesPrice: 16000, rentalDailyRate: 0, rentalWeeklyRate: 0, rentalMonthlyRate: 0, rentalMinimumDays: 1, activeSince: '2021-05-01', activeUntil: null, nextUvvDate: isoDate(20), lastUvvDate: isoDate(-345), createdAt: now, updatedAt: now,
    },
    {
      id: `${tenantId}-machine-2`, tenantId, number: `${prefix}0002`, typeId: types[1].id, typeName: types[1].name,
      manufacturer: 'Jungheinrich', model: 'ETV 225', serialNumber: `JH-${prefix}002`, constructionYear: 2020,
      status: 'stock', customerId: null, customerNumber: '', customerName: '', location: 'Lager', notes: '',
      attributes: { capacity: 2500, liftHeight: 9500, hours: 4120, camera: true }, imageUrl: '', purchasePrice: 8000, salesPrice: 14900, rentalDailyRate: 0, rentalWeeklyRate: 0, rentalMonthlyRate: 0, rentalMinimumDays: 1, activeSince: '2023-02-15', activeUntil: null, createdAt: now, updatedAt: now,
    },
    {
      id: `${tenantId}-machine-3`, tenantId, number: `${prefix}0003`, typeId: types[2].id, typeName: types[2].name,
      manufacturer: 'Linde', model: 'T20', serialNumber: `LIN-${prefix}003`, constructionYear: 2022,
      status: 'rental', customerId: null, customerNumber: '', customerName: '', location: 'Mietpark', notes: '',
      attributes: { capacity: 2000, forkLength: 1150, drive: 'Elektro' }, imageUrl: '', purchasePrice: 6000, salesPrice: 0, rentalDailyRate: 45, rentalWeeklyRate: 250, rentalMonthlyRate: 850, rentalMinimumDays: 1, activeSince: '2024-09-01', activeUntil: null, nextUvvDate: isoDate(-10), lastUvvDate: isoDate(-375), createdAt: now, updatedAt: now,
    },
  ];
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

type StoredAttachment = MachineAttachmentRecord & { readonly machineId: string };

export class DemoMachinesGateway implements MachinesGateway {
  private readonly machines = new Map<string, MachineDetailRecord[]>();
  private readonly machineTypes = new Map<string, MachineTypeAdminRecord[]>();
  private readonly attachments = new Map<string, StoredAttachment[]>();

  private attachmentStore(tenantId: string): StoredAttachment[] {
    const existing = this.attachments.get(tenantId);
    if (existing) return existing;
    const created: StoredAttachment[] = [];
    this.attachments.set(tenantId, created);
    return created;
  }

  private types(tenantId: string): MachineTypeAdminRecord[] {
    const existing = this.machineTypes.get(tenantId);
    if (existing) return existing;
    const created = seedTypes(tenantId);
    this.machineTypes.set(tenantId, created);
    return created;
  }

  private state(tenantId: string): MachineDetailRecord[] {
    const existing = this.machines.get(tenantId);
    if (existing) return existing;
    const created = createSeed(tenantId, this.types(tenantId), customersFor(tenantId));
    this.machines.set(tenantId, created);
    return created;
  }

  private type(tenantId: string, typeId: string): MachineTypeRecord {
    const found = this.types(tenantId).find((item) => item.id === typeId && item.active);
    if (!found) throw new Error('Maschinentyp wurde in diesem Mandanten nicht gefunden.');
    return runtimeType(found);
  }

  private customer(tenantId: string, customerId: string | null | undefined): MachineCustomerOption | null {
    if (!customerId) return null;
    const found = customersFor(tenantId).find((item) => item.id === customerId);
    if (!found) throw new Error('Kunde wurde in diesem Mandanten nicht gefunden.');
    return found;
  }

  private catalog(tenantId: string): readonly MachineTypeAdminRecord[] {
    const machines = this.state(tenantId);
    return this.types(tenantId)
      .map((item) => ({
        ...item,
        machineCount: machines.filter((machine) => machine.typeId === item.id).length,
        fields: [...item.fields].sort((left, right) => left.sortOrder - right.sortOrder),
      }))
      .sort((left, right) => left.name.localeCompare(right.name, 'de'));
  }

  async listMachineTypes(tenantId: string): Promise<readonly MachineTypeRecord[]> {
    return clone(this.catalog(tenantId).filter((item) => item.active).map(runtimeType));
  }

  async listMachineTypesForAdmin(tenantId: string): Promise<readonly MachineTypeAdminRecord[]> {
    return clone(this.catalog(tenantId));
  }

  async saveMachineType(input: SaveMachineTypeInput): Promise<readonly MachineTypeAdminRecord[]> {
    const normalized = normalizeMachineTypeInput(input);
    const types = this.types(normalized.tenantId);
    const machines = this.state(normalized.tenantId);
    const existingIndex = normalized.machineTypeId
      ? types.findIndex((item) => item.id === normalized.machineTypeId)
      : -1;
    const existing = existingIndex >= 0 ? types[existingIndex] : null;

    if (normalized.machineTypeId && !existing) {
      throw new Error('Maschinentyp wurde in diesem Mandanten nicht gefunden.');
    }
    if (types.some((item) => item.id !== existing?.id && item.code === normalized.code)) {
      throw new Error('Der Typ-Code ist in diesem Mandanten bereits vergeben.');
    }

    const machineCount = existing
      ? machines.filter((machine) => machine.typeId === existing.id).length
      : 0;
    if (existing && normalized.active === false && machineCount > 0) {
      throw new Error('Ein verwendeter Maschinentyp kann nicht deaktiviert werden.');
    }

    if (existing) {
      for (const previousField of existing.fields) {
        const nextField = normalized.fields.find((item) => item.id === previousField.id);
        const usedMachines = machines.filter((machine) => (
          machine.typeId === existing.id && hasValue(machine.attributes[previousField.key])
        ));
        if (usedMachines.length > 0 && (
          !nextField
          || nextField.key !== previousField.key
          || nextField.type !== previousField.type
        )) {
          throw new Error(`${previousField.label} wird bereits von Maschinen verwendet und kann nicht entfernt oder umtypisiert werden. (Deaktivieren ist möglich.)`);
        }
      }
    }

    const nextFields: MachineTypeAdminFieldRecord[] = normalized.fields.map((item) => {
      const previous = existing?.fields.find((fieldItem) => fieldItem.id === item.id);
      const next: MachineTypeAdminFieldRecord = {
        id: item.id ?? `${normalized.tenantId}-type-field-${crypto.randomUUID()}`,
        key: item.key,
        label: item.label,
        type: item.type,
        unit: item.unit ?? '',
        required: Boolean(item.required),
        options: item.options ?? [],
        sortOrder: item.sortOrder ?? 0,
        active: item.active ?? true,
        group: item.group ?? '',
      };

      const typeMachines = existing ? machines.filter((machine) => machine.typeId === existing.id) : [];
      if (next.active && next.required && typeMachines.some((machine) => !hasValue(machine.attributes[next.key]))) {
        throw new Error(`${next.label} kann nicht zum Pflichtfeld werden, solange bestehende Maschinen keinen Wert besitzen.`);
      }
      if (next.active && next.type === 'select' && typeMachines.some((machine) => {
        const value = machine.attributes[previous?.key ?? next.key];
        return hasValue(value) && !next.options.includes(String(value));
      })) {
        throw new Error(`${next.label} enthält bestehende Maschinenwerte, die nicht mehr als Auswahl erlaubt wären.`);
      }
      return next;
    });

    const nextType: MachineTypeAdminRecord = {
      id: existing?.id ?? `${normalized.tenantId}-type-${crypto.randomUUID()}`,
      code: normalized.code,
      name: normalized.name,
      description: normalized.description ?? '',
      active: normalized.active ?? true,
      machineCount,
      fields: nextFields,
    };
    if (existingIndex >= 0) types[existingIndex] = nextType;
    else types.push(nextType);

    machines.forEach((machine, index) => {
      if (machine.typeId === nextType.id) machines[index] = { ...machine, typeName: nextType.name };
    });
    return clone(this.catalog(normalized.tenantId));
  }

  async searchCustomerOptions(tenantId: string, query = ''): Promise<readonly MachineCustomerOption[]> {
    const needle = query.trim().toLocaleLowerCase('de');
    return clone(customersFor(tenantId).filter((customer) => !needle || [customer.number, customer.displayName, customer.city]
      .some((value) => value.toLocaleLowerCase('de').includes(needle))));
  }

  async searchMachines(input: MachineSearchInput): Promise<MachinePage> {
    const normalized = normalizeMachineSearchInput(input);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = this.state(normalized.tenantId)
      .filter((machine) => normalized.status === 'all' || machine.status === normalized.status)
      .filter((machine) => normalized.typeId === 'all' || machine.typeId === normalized.typeId)
      .filter((machine) => !normalized.customerNumber || machine.customerNumber === normalized.customerNumber)
      .filter((machine) => {
        if (!normalized.bestandFrom && !normalized.bestandTo) return true;
        const from = normalized.bestandFrom || '0000-01-01';
        const to = normalized.bestandTo || '9999-12-31';
        return machine.activeSince <= to && (machine.activeUntil === null || machine.activeUntil >= from);
      })
      .filter((machine) => !needle || [machine.number, machine.manufacturer, machine.model, machine.serialNumber, machine.customerName]
        .some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .sort((left, right) => left.number.localeCompare(right.number, 'de'));
    const start = (normalized.page - 1) * normalized.pageSize;
    return { items: clone(filtered.slice(start, start + normalized.pageSize).map(listRecord)), total: filtered.length, page: normalized.page, pageSize: normalized.pageSize };
  }

  async getMachine(tenantId: string, machineId: string): Promise<MachineDetailRecord> {
    const found = this.state(tenantId).find((machine) => machine.id === machineId);
    if (!found) throw new Error('Maschine wurde in diesem Mandanten nicht gefunden.');
    return clone(found);
  }

  async createMachine(input: CreateMachineInput): Promise<MachineDetailRecord> {
    const machines = this.state(input.tenantId);
    const type = this.type(input.tenantId, input.typeId);
    const customer = this.customer(input.tenantId, input.customerId);
    const serialNumber = clean(input.serialNumber);
    if (serialNumber && machines.some((machine) => machine.serialNumber.toLocaleLowerCase('de') === serialNumber.toLocaleLowerCase('de'))) {
      throw new Error('Die Seriennummer ist diesem Mandanten bereits zugeordnet.');
    }
    const prefix = prefixFor(input.tenantId);
    const next = machines.reduce((value, machine) => Math.max(value, Number(machine.number.slice(1)) || 0), 0) + 1;
    const now = new Date().toISOString();
    const created: MachineDetailRecord = {
      id: `${input.tenantId}-machine-${crypto.randomUUID()}`, tenantId: input.tenantId,
      number: `${prefix}${String(next).padStart(4, '0')}`, typeId: type.id, typeName: type.name,
      manufacturer: clean(input.manufacturer), model: clean(input.model), serialNumber,
      constructionYear: input.constructionYear ?? null, status: input.status ?? 'customer',
      customerId: customer?.id ?? null, customerNumber: customer?.number ?? '', customerName: customer?.displayName ?? '',
      location: clean(input.location), notes: clean(input.notes), attributes: validateMachineAttributes(type, input.attributes),
      imageUrl: (input.imageUrl ?? '').trim(), purchasePrice: Math.max(0, input.purchasePrice ?? 0), salesPrice: Math.max(0, input.salesPrice ?? 0),
      rentalDailyRate: Math.max(0, input.rentalDailyRate ?? 0), rentalWeeklyRate: Math.max(0, input.rentalWeeklyRate ?? 0),
      rentalMonthlyRate: Math.max(0, input.rentalMonthlyRate ?? 0), rentalMinimumDays: Math.max(1, input.rentalMinimumDays ?? 1),
      activeSince: (input.activeSince ?? '').trim() || now.slice(0, 10), activeUntil: null,
      warengruppeId: input.warengruppeId ?? null,
      warengruppeName: demoWarengruppeName(input.warengruppeId),
      nextUvvDate: null, lastUvvDate: null,
      createdAt: now, updatedAt: now,
    };
    machines.push(created);
    return clone(created);
  }

  async updateMachine(input: UpdateMachineInput): Promise<MachineDetailRecord> {
    const machines = this.state(input.tenantId);
    const index = machines.findIndex((machine) => machine.id === input.machineId);
    if (index < 0) throw new Error('Maschine wurde in diesem Mandanten nicht gefunden.');
    const type = this.type(input.tenantId, input.typeId);
    const customer = this.customer(input.tenantId, input.customerId);
    const serialNumber = clean(input.serialNumber);
    if (serialNumber && machines.some((machine) => machine.id !== input.machineId && machine.serialNumber.toLocaleLowerCase('de') === serialNumber.toLocaleLowerCase('de'))) {
      throw new Error('Die Seriennummer ist einer anderen Maschine dieses Mandanten zugeordnet.');
    }
    const current = machines[index];
    const updated: MachineDetailRecord = {
      ...current, typeId: type.id, typeName: type.name, manufacturer: clean(input.manufacturer), model: clean(input.model),
      serialNumber, constructionYear: input.constructionYear ?? null, status: input.status ?? current.status,
      customerId: customer?.id ?? null, customerNumber: customer?.number ?? '', customerName: customer?.displayName ?? '',
      location: clean(input.location), notes: clean(input.notes), attributes: validateMachineAttributes(type, input.attributes),
      imageUrl: input.imageUrl !== undefined ? input.imageUrl.trim() : current.imageUrl,
      purchasePrice: input.purchasePrice !== undefined ? Math.max(0, input.purchasePrice) : current.purchasePrice,
      salesPrice: input.salesPrice !== undefined ? Math.max(0, input.salesPrice) : current.salesPrice,
      rentalDailyRate: input.rentalDailyRate !== undefined ? Math.max(0, input.rentalDailyRate) : current.rentalDailyRate,
      rentalWeeklyRate: input.rentalWeeklyRate !== undefined ? Math.max(0, input.rentalWeeklyRate) : current.rentalWeeklyRate,
      rentalMonthlyRate: input.rentalMonthlyRate !== undefined ? Math.max(0, input.rentalMonthlyRate) : current.rentalMonthlyRate,
      rentalMinimumDays: input.rentalMinimumDays !== undefined ? Math.max(1, input.rentalMinimumDays) : current.rentalMinimumDays,
      warengruppeId: input.warengruppeId !== undefined ? (input.warengruppeId ?? null) : (current.warengruppeId ?? null),
      warengruppeName: demoWarengruppeName(input.warengruppeId !== undefined ? input.warengruppeId : current.warengruppeId),
      updatedAt: new Date().toISOString(),
    };
    machines[index] = updated;
    return clone(updated);
  }

  // Die Liste liefert Bild-Inhalte mit, Dokument-Inhalte nicht (wie die RPC) -- Download über getAttachment.
  private withoutDocumentContent(record: StoredAttachment): MachineAttachmentRecord {
    const { machineId: _machineId, ...rest } = record;
    return rest.kind === 'image' ? rest : { ...rest, content: '' };
  }

  async listAttachments(tenantId: string, machineId: string): Promise<readonly MachineAttachmentRecord[]> {
    return clone(this.attachmentStore(tenantId)
      .filter((attachment) => attachment.machineId === machineId)
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
      .map((attachment) => this.withoutDocumentContent(attachment)));
  }

  async getAttachment(tenantId: string, attachmentId: string): Promise<MachineAttachmentRecord> {
    const found = this.attachmentStore(tenantId).find((attachment) => attachment.id === attachmentId);
    if (!found) throw new Error('Anhang wurde in diesem Mandanten nicht gefunden.');
    const { machineId: _machineId, ...rest } = found;
    return clone(rest);
  }

  async addAttachment(input: AddMachineAttachmentInput): Promise<MachineAttachmentRecord> {
    if (!this.state(input.tenantId).some((machine) => machine.id === input.machineId)) {
      throw new Error('Maschine wurde in diesem Mandanten nicht gefunden.');
    }
    if (input.kind !== 'image' && input.kind !== 'document') throw new Error('Ungültige Anhangsart.');
    const title = clean(input.title);
    if (!title) throw new Error('Der Anhang benötigt einen Titel.');
    if (!input.content) throw new Error('Der Anhang benötigt einen Inhalt.');
    const record: StoredAttachment = {
      id: `${input.tenantId}-att-${crypto.randomUUID()}`, machineId: input.machineId,
      kind: input.kind, title, mimeType: input.mimeType ?? '', byteSize: Math.max(0, input.byteSize ?? 0),
      createdAt: new Date().toISOString(), content: input.content,
    };
    this.attachmentStore(input.tenantId).push(record);
    return clone(this.withoutDocumentContent(record));
  }

  async removeAttachment(tenantId: string, attachmentId: string): Promise<void> {
    const store = this.attachmentStore(tenantId);
    const index = store.findIndex((attachment) => attachment.id === attachmentId);
    if (index < 0) throw new Error('Anhang wurde in diesem Mandanten nicht gefunden.');
    store.splice(index, 1);
  }

  async updateMachineNotes(tenantId: string, machineId: string, notes: string): Promise<MachineDetailRecord> {
    const machines = this.state(tenantId);
    const index = machines.findIndex((machine) => machine.id === machineId);
    if (index < 0) throw new Error('Maschine wurde in diesem Mandanten nicht gefunden.');
    machines[index] = { ...machines[index], notes: clean(notes), updatedAt: new Date().toISOString() };
    return clone(machines[index]);
  }

  async setMachineTaxScheme(tenantId: string, machineId: string, scheme: MachineTaxScheme): Promise<MachineDetailRecord> {
    const machines = this.state(tenantId);
    const index = machines.findIndex((machine) => machine.id === machineId);
    if (index < 0) throw new Error('Maschine wurde in diesem Mandanten nicht gefunden.');
    machines[index] = { ...machines[index], taxScheme: scheme, updatedAt: new Date().toISOString() };
    return clone(machines[index]);
  }

  async setMachineInspection(
    tenantId: string,
    machineId: string,
    nextUvvDate: string | null,
    lastUvvDate: string | null,
  ): Promise<MachineDetailRecord> {
    const machines = this.state(tenantId);
    const index = machines.findIndex((machine) => machine.id === machineId);
    if (index < 0) throw new Error('Maschine wurde in diesem Mandanten nicht gefunden.');
    machines[index] = {
      ...machines[index],
      nextUvvDate: nextUvvDate || null,
      lastUvvDate: lastUvvDate || null,
      updatedAt: new Date().toISOString(),
    };
    return clone(machines[index]);
  }

  /**
   * Entfernt einen Datensatz -- das Gegenstueck zum Loeschen in der Datenbank.
   * Ohne das meldet der Demo-Modus "geloescht", waehrend der Eintrag stehen bleibt.
   */
  removeMachine(tenantId: string, machineId: string): void {
    const liste = this.machines.get(tenantId);
    if (!liste) return;
    const stelle = liste.findIndex((eintrag) => eintrag.id === machineId);
    if (stelle >= 0) liste.splice(stelle, 1);
  }
}

export function createDemoMachinesGateway(): DemoMachinesGateway { return new DemoMachinesGateway(); }
